import type { Wallet } from "@/app/types/wallet";
import {
  clearWalletData,
  getStoredWalletData,
  storeWalletData,
} from "./walletUtils";
import { WalletProviderType } from "../services/enum/wallet-provider-type";
import { MetamaskErrorCodes } from "../services/enum/metamask-error-codes";
import { ethers } from "ethers";
import { ExternalProvider } from "@ethersproject/providers";
import getDeviceType from "../device/getDeviceType";
import CoinbaseWalletSDK, {
  type ProviderInterface,
} from "@coinbase/wallet-sdk";

type CoinbaseProvider = ProviderInterface & ExternalProvider;

export class Coinbase implements Wallet {
  activeProvider: undefined | ethers.providers.Web3Provider;
  cbwProvider: ProviderInterface | undefined;
  constructor() {
    const coinbaseWallet = new CoinbaseWalletSDK({
      appName: "Sleap.io",
      appChainIds: [8453],
    });

    this.cbwProvider = coinbaseWallet.makeWeb3Provider({
      options: "all",
      attribution: { auto: true },
    });
  }

  async connect() {
    const accounts = (await this.cbwProvider?.request({
      method: "eth_requestAccounts",
    })) as string[] | undefined;
    const walletId = accounts?.[0];
    if (!walletId) {
      return "";
    }

    const response = this.persistLastUsedWallet(
      WalletProviderType.COINBASE,
      walletId
    );
    return response;
  }

  async disconnect(): Promise<void> {
    try {
      await this.cbwProvider?.disconnect();
      clearWalletData();
    } catch (error) {
      console.warn(error);
    }
  }

  async signTransaction(
    walletId: string,
    challengeMessage: string
  ): Promise<string> {
    try {
      const signature: unknown = await this.cbwProvider?.request({
        method: "personal_sign",
        params: [challengeMessage, walletId],
      })!;
      return Promise.resolve(signature as string);
    } catch (e) {
      return Promise.reject("unable to sign Message");
    }
  }

  async isConnected() {
    const walletProviderType = WalletProviderType.COINBASE;
    const { walletAddress } = getStoredWalletData();

    if (walletAddress && walletProviderType) {
      const sdkAccounts = (await this.cbwProvider
        ?.request({ method: "eth_accounts" })
        .catch(() => [])) as string[] | undefined;
      if (sdkAccounts?.[0] && this.cbwProvider) {
        return { accounts: sdkAccounts, providerType: walletProviderType };
      }

      const { isMobileType, isTabletType } = getDeviceType();
      const isMobile = isMobileType || isTabletType;
      const injectedProvider = this.getWalletProvider(
        walletProviderType,
        isMobile
      ) as CoinbaseProvider | undefined;

      if (injectedProvider) {
        const provider = new ethers.providers.Web3Provider(injectedProvider);
        const acc = await provider.listAccounts();
        if (acc.length > 0) {
          return { accounts: acc, providerType: walletProviderType };
        }
      }
    }

    return "";
  }

  async getAddress() {
    return "";
  }

  persistLastUsedWallet = (
    walletProviderType?: WalletProviderType,
    walletAddress?: string
  ) => {
    if (!walletProviderType || !walletAddress) {
      clearWalletData();
      return "";
    } else {
      const walletId = getStoredWalletData().walletAddress;
      const walletType = getStoredWalletData().walletProviderType;
      if (walletId === walletAddress && walletType === walletProviderType) {
        return walletId;
      }

      storeWalletData(walletProviderType, walletAddress!);
      return walletAddress;
    }
  };

  getWalletProvider(walletProvider: WalletProviderType, isMobile: boolean) {
    let provider: ExternalProvider | undefined;
    // @ts-ignore
    if (window?.ethereum?.providers?.length) {
      // @ts-ignore
      window.ethereum.providers.forEach(async (p) => {
        switch (walletProvider) {
          case WalletProviderType.COINBASE:
            if (p.isCoinbaseWallet) provider = p;
            break;
          default:
            return null;
        }
        return provider;
      });
    } else {
      switch (walletProvider) {
        case WalletProviderType.COINBASE:
          // @ts-ignore
          provider = this.getCoinbaseProviderForBoth(isMobile);
          break;
      }
    }
    return provider;
  }

  getCoinbaseProviderForBoth(isMobile: boolean) {
    let provider;
    if (isMobile) {
      // @ts-ignore
      if (window?.ethereum && window?.ethereum?.isCoinbaseWallet) {
        provider = window.ethereum;
        return provider;
      } else {
        return null;
      }
    } else {
      if (window?.coinbaseWalletExtension) {
        provider = window.coinbaseWalletExtension;
        return provider;
      } else {
        return null;
      }
    }
  }

  handleWalletError(errorCode: string): string {
    let error = "";
    switch (errorCode) {
      case MetamaskErrorCodes.NO_METAMASK_PLUGIN: {
        error = "No metamask plugin found.";
        break;
      }
      case "ACTION_REJECTED": {
        error = "You rejected the transaction.";
        break;
      }
      case MetamaskErrorCodes.NO_WALLET_CONNECTED: {
        error = "You rejected the transaction.";
        break;
      }
      case MetamaskErrorCodes.WALLET_DISCONNECTED: {
        error = "Wallet has been disconnected.";
        break;
      }
      default: {
        error = "Unknown error while connecting your metamask wallet";
      }
    }
    return error;
  }

  getConnectedWallet(): WalletProviderType {
    return WalletProviderType.COINBASE;
  }
}
